import React from 'react';
import { Modal, ListGroup, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';
import CalendarEventRow from './CalendarEventRow';

/**
 * Modal que muestra todos los eventos de un día específico
 *
 * Se abre cuando:
 * - Hay más de 3 eventos en un día y se hace click
 * - Se hace click en el indicador "+X más"
 *
 * Muestra:
 * - Fecha del día
 * - Lista completa de eventos con título, descripción y monto
 * - Badge con cantidad de eventos
 *
 * Cada evento es clickeable y navega a su entidad relacionada. Los eventos
 * nativos de Google admiten editar/eliminar; los eventos de la app (cuotas,
 * gastos) admiten editar solo la fecha — ver getAppEventEditTarget.
 *
 * @param {boolean} show - Si el modal está visible
 * @param {Function} onHide - Callback para cerrar el modal
 * @param {Date} day - Fecha del día
 * @param {Array} events - Array de eventos para este día
 * @param {Function} onEditGoogleEvent - (event) => void — edita un evento nativo de Google
 * @param {Function} onDeleteGoogleEvent - (event) => void — elimina un evento nativo de Google
 * @param {Function} onEditAppEvent - (event) => void — edita la fecha de un evento de la app
 */
function CalendarDayModal({ show, onHide, day, events, onEditGoogleEvent, onDeleteGoogleEvent, onEditAppEvent }) {
	const navigate = useNavigate();

	const handleEventClick = (event) => {
		if (event.externalLink) {
			window.open(event.externalLink, '_blank', 'noopener');
		} else if (event.link) {
			navigate(event.link);
			onHide();
		}
	};

	return (
		<Modal show={show} onHide={onHide} size='lg'>
			<Modal.Header closeButton>
				<Modal.Title>
					Eventos del {day ? moment.utc(day).format('DD [de] MMMM [de] YYYY') : ''}
					{events && events.length > 0 && (
						<Badge bg='info' className='ms-2'>
							{events.length} evento{events.length !== 1 ? 's' : ''}
						</Badge>
					)}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{events && events.length > 0 ? (
					<ListGroup variant='flush'>
						{events.map((event, index) => (
							<ListGroup.Item
								key={index}
								action
								onClick={() => handleEventClick(event)}
								style={{ cursor: 'pointer' }}
							>
								<CalendarEventRow
									event={event}
									onEditGoogleEvent={onEditGoogleEvent}
									onDeleteGoogleEvent={onDeleteGoogleEvent}
									onEditAppEvent={onEditAppEvent}
								/>
							</ListGroup.Item>
						))}
					</ListGroup>
				) : (
					<p className='text-muted text-center'>No hay eventos para este día</p>
				)}
			</Modal.Body>
		</Modal>
	);
}

export default CalendarDayModal;

